import { useParams } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { Base, toast } from '@itsrever/design-system'
import { EditProcessFormData } from './edit-process-form/edit-process-form'
import { useUpdateProcess } from '@/hooks/use-update-process'
import { type ReturnsAvailableRefundMethodChangeOptionsResponse } from '@itsrever/rever-api'
import {
  EXCHANGE_METHOD,
  isStillApplying,
  toUpdateRequest
} from './edit-process-form/refund-method-options'

const {
  DialogClose,
  DialogTitle,
  DialogDescription,
  Button,
  DialogHeader,
  DialogFooter
} = Base

interface EditProcessConfirmationProps {
  onClose: () => void
  data: EditProcessFormData
  refundMethods?: ReturnsAvailableRefundMethodChangeOptionsResponse
}

export const EditProcessConfirmation = ({
  onClose,
  data,
  refundMethods
}: EditProcessConfirmationProps) => {
  const { t } = useTranslation()

  const { id: processId } = useParams()

  const { mutate: updateProcess, status } = useUpdateProcess()

  const changesExchange =
    refundMethods?.from_exchange === true && data.method !== EXCHANGE_METHOD

  const onEditReturnProcess = () => {
    if (processId) {
      updateProcess(
        {
          processId,
          returnsUpdateReturnProcessRequest: {
            process_id: processId,
            ...toUpdateRequest(refundMethods, data),
            amount: parseFloat(data.amount),
            reason: data.reason
          }
        },
        {
          onSuccess: () => {
            toast({
              variant: 'success',
              text: t('process_details_page.edit_process_success')
            })

            window.location.reload()
          },
          onError: (error) => {
            if (isStillApplying(error)) {
              toast({
                variant: 'info',
                text: t('process_details_page.edit_process_still_applying')
              })
            } else {
              toast({
                variant: 'error',
                text: t('review_toast.error')
              })
            }

            onClose()
          }
        }
      )
    }
  }

  return (
    <div className="w-[28em] space-y-7">
      <div className="space-y-4">
        <DialogHeader>
          <DialogTitle>
            {t('process_details_page.confirm_refund_title')}
          </DialogTitle>
          <DialogDescription>
            {t('process_details_page.confirm_refund_description')}
          </DialogDescription>
        </DialogHeader>
        {changesExchange ? (
          <p className="text-sm">
            {t('process_details_page.exchange_to_opm_confirmation')}
          </p>
        ) : null}
        {changesExchange && refundMethods?.refunds_checkout_surplus ? (
          <p className="text-sm">
            {t('process_details_page.exchange_to_opm_surplus_refund')}
          </p>
        ) : null}
        <dl className="divide-y-1 rounded-md border border-solid border-neutral-200 bg-neutral-50 p-4 text-sm">
          <div className="space-y-2 pb-4">
            <div className="item-center flex justify-between gap-4">
              <dt className="text-sm text-neutral-600">
                {t('process_details_page.refund_method_label')}
              </dt>
              <dd className="text-sm">{t(`refund_methods.${data.method}`)}</dd>
            </div>
            <div className="item-center flex justify-between gap-4">
              <dt className="text-sm text-neutral-600">
                <Trans
                  i18nKey="process_details_page.refund_amount_label"
                  values={{ currency: data.currency }}
                />
              </dt>
              <dd className="text-sm">{parseFloat(data.amount).toFixed(2)}</dd>
            </div>
          </div>
          <div className="item-center flex flex-col justify-between gap-1 border-t border-solid border-neutral-200 pt-4">
            <dt className="text-sm text-neutral-600">
              {t('process_details_page.reason_for_adjustment_label')}
            </dt>
            <dd className="text-sm font-normal">{data.reason}</dd>
          </div>
        </dl>
      </div>
      <DialogFooter>
        <DialogClose render={<Button variant="secondary" type="button" />}>
          {t('process_details_page.cancel')}
        </DialogClose>
        <Button
          variant="primary"
          type="button"
          onClick={onEditReturnProcess}
          loading={status === 'pending'}
        >
          {t('process_details_page.confirm_action')}
        </Button>
      </DialogFooter>
    </div>
  )
}
